import { Button, Typography } from '@breetometer/components';
import { CurrencyBalance, InfoIcon } from 'BreetAssets';
import { type IDIALOGVERIFYTRANSACTIONPROPS, Loader, Tooltip, TooltipContent, TransactionDetails } from 'BreetComponents';
import {
	alwaysRefetchApiQueryOptions,
	convertCryptoInputAmount,
	type CURRENCYCODETYPE,
	getFiatWithdrawFee,
	type ITransactionDetailsRow,
	showToast,
} from 'BreetHelpers';
import {
	useCurrencyOutput,
	useFiatWithdrawRequestContext,
	useModalApi,
	useUserCurrencyProps,
	useWithdrawRequestContext,
} from 'BreetHooks';
import { useGetUserPreferenceQuery } from 'BreetRedux';
import { useCallback } from 'react';

const TransactionDetailsSection = () => {
	const { selectedWallet } = useWithdrawRequestContext();
	const { userWalletProps, withdrawRequestHookForm, meta } = useFiatWithdrawRequestContext();

	const { isLoading: userPreferenceLoading } = useGetUserPreferenceQuery(undefined, alwaysRefetchApiQueryOptions);

	const { openModalDirect } = useModalApi();

	const currencyOutput = useCurrencyOutput();
	const currencyProps = useUserCurrencyProps();

	const balance = convertCryptoInputAmount(userWalletProps.walletBalance);

	const { watch: withdrawRequestWatch } = withdrawRequestHookForm;

	const amount = withdrawRequestWatch('amount');
	const narration = withdrawRequestWatch('narration');
	const selectedBeneficiary = withdrawRequestWatch('selectedBeneficiary');

	const amountNum = isNaN(Number(amount)) ? 0 : Number(amount);

	const walletCurrencyCode = selectedWallet?.currency ?? currencyProps?.code;

	const withdrawalFee = getFiatWithdrawFee(meta?.settings, walletCurrencyCode);

	const code = userWalletProps.code as unknown as CURRENCYCODETYPE;

	const totalDeducted = `${userWalletProps.symbol} ${currencyOutput(withdrawalFee + amountNum)}`;

	const isInsufficientFunds = withdrawalFee + amountNum > userWalletProps.walletBalance;

	const withdrawDetailsContent: ITransactionDetailsRow[] = [
		{
			label: 'Amount',
			value: `${userWalletProps.symbol} ${currencyOutput(amount)}`,
		},
		{
			label: 'Bank Account',
			bankDetails: {
				accountName: selectedBeneficiary.accountName,
				accountNumber: selectedBeneficiary.accountNumber,
				bankName: selectedBeneficiary.bankName,
			},
		},
	];

	const withdrawFunds = useCallback(() => {
		if (!selectedBeneficiary.id) {
			showToast('error', 'Please select a beneficiary before proceeding');
			return;
		}

		const metadata = {
			transactionType: 'withdraw-fiat',
			withdrawFiatReqData: {
				amount: Number(amount) || 0,
				bankId: selectedBeneficiary.id,
				narration,
			},
		} as IDIALOGVERIFYTRANSACTIONPROPS;

		openModalDirect({
			id: 'verifyTransaction',
			metadata,
			variant: 'right-sheet',
		});
	}, [amount, narration, openModalDirect, selectedBeneficiary.id]);

	if (userPreferenceLoading) return <Loader />;

	return (
		<div className='transaction-details'>
			<TransactionDetails.Content>
				{withdrawDetailsContent.map((row) => (
					<TransactionDetails.Row key={row.label}>
						<TransactionDetails.RowLeft label={row.label} />

						<TransactionDetails.RowRight>
							{!!row.value && (
								<Typography className='transaction-details_content_row_value color-neutral-50'>{row.value}</Typography>
							)}
							{!!row.bankDetails && (
								<>
									<Typography className='transaction-details_content_row_accountname color-neutral-50'>
										{row.bankDetails.accountNumber}
									</Typography>
									<Typography
										as='span'
										className='transaction-details_content_row_accountdetails color-primary-50'
									>
										{row.bankDetails.bankName}
									</Typography>
									<Typography
										as='span'
										className='transaction-details_content_row_accountdetails color-primary-50'
									>
										{row.bankDetails.accountName}
									</Typography>
								</>
							)}
						</TransactionDetails.RowRight>
					</TransactionDetails.Row>
				))}

				{code === 'NGN' && (
					<TransactionDetails.Row>
						<div className='content-row-left content-row-left-narration'>
							<Typography className='transaction-details_content_row_label color-primary-50'>Withdrawal Fees</Typography>
							<Tooltip tipElement={<TooltipContent content='This fee covers stamp duty and charges paid to the bank.' />}>
								<InfoIcon className='info-icon svg-light' />
							</Tooltip>
						</div>
						<TransactionDetails.RowRight value={withdrawalFee} />
					</TransactionDetails.Row>
				)}

				{code === 'NGN' && (
					<TransactionDetails.Row
						label='Total Debit'
						value={totalDeducted}
					/>
				)}

				{narration && (
					<TransactionDetails.Row
						label='Narration'
						value={narration}
					/>
				)}
			</TransactionDetails.Content>

			<CurrencyBalance
				variant={code}
				balance={balance}
				className='svg-dark svg-dark-outline'
			/>

			{isInsufficientFunds && (
				<div className='transaction-details_insufficient-funds bg-dark-maroon'>
					<Typography className='color-error-100'>
						Insufficient Balance! Receive more crypto to complete your withdrawal.
					</Typography>
				</div>
			)}

			<Button
				label='Withdraw Funds'
				onClick={withdrawFunds}
				className='right-sheet_cta_fullwidth'
				type='button'
				disabled={isInsufficientFunds}
			/>
		</div>
	);
};

export default TransactionDetailsSection;
